import { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { ShoppingCart, Radio, Store, Package, Truck } from 'lucide-react';
import { api, fcfa } from '../lib/api';
import { cart, imgSrc } from '../lib/cart';
import { CategoryBadge } from '../lib/categories';
import Header from '../components/Header';
import { VerifiedBadge } from '../components/VerifiedBadge';
import { ProductReviews, Stars } from '../components/ProductReviews';

export default function ProductPage() {
  const { productId } = useParams();
  const nav = useNavigate();
  const [p, setP] = useState(null);
  const [img, setImg] = useState(0);
  const [err, setErr] = useState(false);
  useEffect(() => { api.get(`/products/${productId}`).then(({ data }) => setP(data)).catch(() => setErr(true)); }, [productId]);
  if (err) return <div className="min-h-screen"><Header /><div className="p-10 text-slate-400">Article introuvable. <Link to="/shop" className="text-sky-400">Retour à la boutique</Link></div></div>;
  if (!p) return <div className="min-h-screen"><Header /><div className="p-10 text-slate-400">Chargement…</div></div>;
  const images = (p.images?.length ? p.images : [p.image_url]).filter(Boolean);
  const out = !(p.stock > 0) || !(p.price > 0);
  const add = () => { cart.add(p); toast.success('Ajouté au panier'); };
  return (
    <div className="min-h-screen">
      <Header />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 grid lg:grid-cols-2 gap-8">
        <div>
          <div className="aspect-square rounded-3xl overflow-hidden bg-slate-950 border border-slate-800">
            {images[img] ? <img data-testid="product-main-image" src={imgSrc(images[img])} alt={p.name} className="h-full w-full object-cover" /> : <div className="h-full grid place-items-center text-slate-600">Pas de photo</div>}
          </div>
          {images.length > 1 && (
            <div className="mt-3 flex gap-2 overflow-x-auto" data-testid="product-thumbs">
              {images.map((u, i) => <button key={u} onClick={() => setImg(i)} className={`h-16 w-16 rounded-xl overflow-hidden border-2 shrink-0 ${i === img ? 'border-sky-400' : 'border-transparent'}`}><img src={imgSrc(u)} alt="" className="h-full w-full object-cover" /></button>)}
            </div>
          )}
        </div>
        <div className="space-y-5">
          <div>
            <CategoryBadge category={p.category} size={p.size} brand={p.brand} />
            <h1 className="font-display text-3xl font-extrabold mt-2" data-testid="product-name">{p.name}</h1>
            {p.rating_count > 0 && <a href="#avis" className="mt-1 inline-flex items-center gap-1.5 text-sm text-amber-300" data-testid="product-rating-badge"><Stars value={p.rating_avg} size="h-3.5 w-3.5" /> {p.rating_avg} · {p.rating_count} avis</a>}
            <Link to={`/seller/${p.seller_id}`} data-testid="product-seller-link" className="mt-1 inline-flex items-center gap-1.5 text-sm text-slate-300 hover:text-amber-400"><Store className="h-4 w-4" />{p.seller_name}<VerifiedBadge verified={p.seller_verified} /></Link>
          </div>
          <div className="font-mono-num text-4xl font-extrabold text-amber-400" data-testid="product-price">{p.price > 0 ? fcfa(p.price) : 'Uniquement en enchère'}</div>
          <div className="text-sm text-slate-400 flex items-center gap-2"><Package className="h-4 w-4" />{p.stock > 0 ? `${p.stock} en stock` : 'Rupture de stock'}</div>
          {p.seller_shop && (
            <div className="text-sm text-slate-300 flex items-start gap-2" data-testid="product-delivery-info"><Truck className="h-4 w-4 text-amber-400 mt-0.5 shrink-0" />
              <span>Livraison partout au Sénégal : le vendeur confirme les frais après votre commande, vous validez le total avant de payer.{p.seller_shop.shop_delivery ? <span className="text-slate-400"> · {p.seller_shop.shop_delivery}</span> : null}</span>
            </div>
          )}
          {p.description && <p className="text-slate-300 text-sm leading-relaxed" data-testid="product-desc">{p.description}</p>}
          <div className="flex flex-wrap gap-2">
            <button disabled={out} onClick={add} data-testid="product-add-cart" className="inline-flex items-center gap-2 px-6 py-3 rounded-2xl btn-brand text-white font-bold disabled:opacity-50"><ShoppingCart className="h-5 w-5" />Ajouter au panier</button>
            <button disabled={out} onClick={() => { cart.add(p); nav('/cart'); }} data-testid="product-buy-now" className="px-6 py-3 rounded-2xl bg-emerald-600 hover:bg-emerald-500 text-white font-bold disabled:opacity-50">Acheter maintenant</button>
          </div>
          {p.live_id && (
            <Link to={`/live/${p.live_id}`} data-testid="product-live-link" className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-red-600 text-white text-sm font-bold glow-live"><Radio className="h-4 w-4" />Ce vendeur est en live — rejoindre</Link>
          )}
          <div className="text-xs text-slate-500">Paiement Wave / Orange Money au vendeur après commande, puis confirmation et livraison.</div>
        </div>
        <ProductReviews productId={p.product_id} />
      </div>
    </div>
  );
}
